import React, { useState, Fragment, useRef } from 'react';
import Localize from 'react-intl-universal';

import { get } from 'lodash-es';
import { v4 as uuidv4 } from 'uuid';

import CloseIcon from '@mui/icons-material/Close';
import DriveFileRenameOutlineIcon from '@mui/icons-material/DriveFileRenameOutline';
import GroupAddIcon from '@mui/icons-material/GroupAdd';
import { alpha } from '@mui/material';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import { styled } from '@mui/material/styles';

import initialValues from '@pages/BookingProcess/ContactInformation/initialValues';

import ParticipantDialogForm from './ParticipantDialogForm/ParticipantDialogForm';

const StyledButton = styled(Button)(({ theme }) => ({
  width: '100%',
  height: '3.8rem',
  backgroundColor: alpha(get(theme.palette.primary, 'main'), 0.8),
  fontWeight: '700',
  fontSize: '1rem',
  color: theme.palette.common.white,
  boxShadow: 'none',
  textTransform: 'none',
  '&:hover': { backgroundColor: alpha(get(theme.palette.primary, 'main'), 0.4), boxShadow: 'none' }
}));

const StyledSaveButton = styled(Button)(({ theme }) => ({
  minWidth: '10rem',
  marginRight: '4rem',
  textTransform: 'none',
  fontWeight: '700',
  fontSize: '14px',
  backgroundColor: theme.palette.primary.main,
  color: theme.palette.common.white,
  '&:hover': { backgroundColor: alpha(get(theme.palette.primary, 'main'), 0.7), boxShadow: 'none' },
  '&.Mui-disabled': { color: theme.palette.common.white, opacity: '0.5' }
}));
const StyledCancelButton = styled(Button)(({ theme }) => ({
  textTransform: 'none',
  fontSize: '14px',
  fontWeight: '700',
  color: theme.palette.primary.main
}));

const StyledDialogTitle = styled(DialogTitle)(({ theme }) => ({
  paddingBottom: '0.3rem',
  fontWeight: '700',
  fontSize: '1.25rem',
  color: theme.palette.booking.header
}));

const StyledEditIcon = styled(DriveFileRenameOutlineIcon)(({ theme }) => ({
  color: theme.palette.primary.main,
  cursor: 'pointer'
}));

const StyledDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialog-paper': {
    backgroundColor: `${theme.palette.booking.background}!important`,
    backgroundImage: `linear-gradient(${alpha(get(theme.palette.common, 'white'), 0.05)}, ${alpha(get(theme.palette.common, 'white'), 0.05)})`,

    [theme.breakpoints.up('xs')]: { width: '100%', margin: '0.5rem' }
  }
}));

const ParticipantDialog = ({
  mode,
  participants,
  setParticipants,
  id = null,
  disableButton,
  configuration = {},
  isEventFree = false,
  isEventIlias
}) => {
  const [open, setOpen] = useState(false);
  const [formData, setFormData] = useState(initialValues);
  const [isFormValid, setIsFormValid] = useState(true);
  const inputRef = useRef(null);

  const handleClickOpen = (event) => {
    if (mode === 'edit') {
      setFormData(
        participants.find((participant) => participant.id === event.currentTarget.id) ?? {}
      );
    }
    setOpen(true);
  };

  const handleClose = () => {
    setFormData(initialValues);
    setOpen(false);
  };
  const handleSaveParticipant = () => {
    inputRef.current.click();
  };

  const participantId = uuidv4();

  return (
    <Fragment>
      {mode === 'create' ? (
        <StyledButton
          data-test-id='create-participant-button'
          variant='contained'
          startIcon={<GroupAddIcon />}
          onClick={handleClickOpen}
          disabled={disableButton}
        >
          {Localize.get('Booking.createNewParticipant')}
        </StyledButton>
      ) : (
        <Button sx={{ minWidth: '1.5rem' }} onClick={handleClickOpen} id={id}>
          <StyledEditIcon />
        </Button>
      )}
      <StyledDialog
        open={open}
        onClose={handleClose}
        aria-labelledby='alert-dialog-title'
        aria-describedby='alert-dialog-description'
        fullWidth={true}
        maxWidth='md'
      >
        <StyledDialogTitle id='alert-dialog-title' sx={{ mt: 0.5, pl: '3.5rem', pr: '3.5rem' }}>
          <GroupAddIcon sx={{ mr: '0.7rem' }} />
          {Localize.get('Booking.createNewParticipant')}
        </StyledDialogTitle>
        <IconButton
          aria-label='close'
          onClick={handleClose}
          sx={{ position: 'absolute', right: 8, top: 8, color: (theme) => theme.palette.grey[500] }}
        >
          <CloseIcon />
        </IconButton>

        <DialogContent
          sx={{
            paddingTop: 0,
            pl: { xs: '0.5rem', sm: '3.5rem' },
            pr: { xs: '0.5rem', sm: '3.5rem' },
            backgroundColor: (theme) => theme.palette.booking.list
          }}
        >
          <ParticipantDialogForm
            configuration={configuration}
            mode={mode}
            inputRef={inputRef}
            setOpen={setOpen}
            setParticipants={setParticipants}
            formData={formData}
            setIsFormValid={setIsFormValid}
            participantId={participantId}
            isEventFree={isEventFree}
            isEventIlias={isEventIlias}
          />
        </DialogContent>
        <DialogActions sx={{ backgroundColor: (theme) => theme.palette.booking.dialogFooter }}>
          <StyledCancelButton onClick={handleClose}>{Localize.get('App.close')}</StyledCancelButton>
          <StyledSaveButton onClick={handleSaveParticipant} disabled={!isFormValid}>
            {Localize.get('Booking.saveParticipant')}
          </StyledSaveButton>
        </DialogActions>
      </StyledDialog>
    </Fragment>
  );
};

export default ParticipantDialog;
